import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { workspaceKey } from './workspaceQueryKey';

const CREDENTIAL_READ = 'credential:read';

/**
 * Probes `GET /me/capabilities?capabilities=credential:read` for the ACTIVE workspace —
 * the switch behind the Credentials nav pillar (steward-app#643) — and reports whether it
 * has answered yet, so a caller with a stale-flash problem (steward-app#1402) can hold
 * paint on that answer instead of on the fail-open default below.
 *
 * Deliberately NOT a `roleIds` check against a mirrored role list, unlike
 * `useCanWrite` (`./useCanWrite.ts`). `useCanWrite`'s gate (`isReadOnlyMember`)
 * predates appcore's capability probe and is documented in
 * `packages/steward-types/src/roles.ts`'s header as a mirror scheduled for deletion
 * (steward-app#436) — a fallback kept alive only for environments where the probe route
 * isn't deployed yet. This hook has no such legacy to carry, so it goes straight to the
 * probe, a thin façade passthrough (`services/steward-app-api/src/handlers/capabilities.ts`)
 * to appcore's own `GET /w/{ws}/me/capabilities`. That answers through the SAME
 * `evaluateRoleGrant` call appcore's enforced gate makes on every credential read, so this
 * and the real gate cannot disagree without appcore itself changing — which a role-id
 * mirror can never promise (see the drift #312 shipped: one read-only role missing from a
 * hand-maintained list, caught only in security review).
 *
 * ⚠️ **UX GATING ONLY, NEVER AUTHORIZATION.** appcore's `credential`/`credential-vault`
 * CTYPEs declare `accessControl.readCapability: 'credential:read'`
 * (`packages/steward-types/src/index.ts`), and THAT is what refuses an advisor's read —
 * on every plane, including a hand-crafted request that never renders this nav item at
 * all. This hook only decides whether to show a pillar that would otherwise 403.
 *
 * `canRead` fails OPEN (`true`) while loading or on any error, matching `useCanWrite`'s
 * asymmetry: hiding a pillar from someone who should see it is the costly mistake for a
 * *visibility* gate (a real writer stripped of a working page is broken), while the
 * actual protection — the appcore-side `readCapability` gate — does not depend on this
 * hook's answer at all. A false positive here costs nothing but an extra nav item that
 * 403s if clicked; appcore-side, an advisor cannot reach a single credential regardless.
 * `settled` does NOT share that asymmetry — it is a plain "has this query left the
 * pending state" fact (`isSuccess || isError`), false while in flight (including while
 * disabled for a missing workspace/token) and true once it has resolved either way. A
 * caller that fails open on `!settled` gets exactly `useCanReadCredentials`'s own
 * behaviour; `Layout.tsx`'s `SidebarContent` is the one caller that does something else
 * with it (holds paint briefly instead), and that choice lives there, not here.
 */
export function useCredentialsReadProbe(): { canRead: boolean; settled: boolean } {
  const token = useAccessToken();
  const { activeWorkspaceId } = useWorkspace();

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: workspaceKey('capabilities', activeWorkspaceId, CREDENTIAL_READ),
    queryFn: () =>
      createApiClient(token!).get<{ capabilities: Record<string, boolean> }>(
        `/me/capabilities?capabilities=${encodeURIComponent(CREDENTIAL_READ)}`,
      ),
    enabled: !!token && !!activeWorkspaceId,
    staleTime: 5 * 60_000,
  });

  const canRead = !activeWorkspaceId || isLoading || isError
    ? true
    // `=== false`, not `!`: an absent key or a non-boolean value must not read as a denial
    // here, matching the same care `writeGate.ts` takes on the server side of this probe.
    : data?.capabilities?.[CREDENTIAL_READ] !== false;

  return { canRead, settled: isSuccess || isError };
}

/** Thin wrapper over {@link useCredentialsReadProbe} for a caller that only ever wants the
 *  fail-open boolean and has no flash to guard against. A `src/` scan on 2026-09-11 (this
 *  change) found no production caller of this wrapper left — `Layout.tsx`'s
 *  `SidebarContent`, the only OTHER caller of either hook, needs `settled` too and so
 *  calls {@link useCredentialsReadProbe} directly — but the wrapper stays exported for
 *  whichever caller reaches for the plain boolean next, same as before this issue. Same
 *  query, same options (react-query dedupes on the key, so this adds no request), just
 *  `.canRead`. */
export function useCanReadCredentials(): boolean {
  return useCredentialsReadProbe().canRead;
}
